import { captureException, captureMessage } from '@sentry/nuxt'

/**
 * Determines if the value is an actual Error instance or exception-like object
 */
function isException(error: any): boolean {
  return (
    error instanceof Error ||
    (error && typeof error === 'object' && 'stack' in error) ||
    (error &&
      typeof error === 'object' &&
      'message' in error &&
      typeof error.message === 'string')
  )
}

/**
 * Sentry error capture utility for @sentry/nuxt
 * Uses direct imports to work from any context (including XState actions)
 * Routes to captureException for Error objects or captureMessage for strings/events
 */
export default function sentryCapture(error: any) {
  try {
    if (isException(error)) {
      captureException(error)
    } else {
      // For non-exception errors (strings, plain objects, events)
      const message = typeof error === 'string' ? error : JSON.stringify(error)
      captureMessage(message, 'error')
    }
  } catch (sentryError) {
    // Fallback to console if anything fails
    console.error('Sentry capture failed:', sentryError)
    console.error('Original error:', error)
    if (error?.message) {
      console.error('Error message:', error.message)
    }
    if (error?.issues) {
      error.issues.forEach((issue: any) => {
        console.error('Issue:', issue)
      })
    }
  }
}

/**
 * Captures messages with Sentry or falls back to console logging
 */
export function sentryCaptureMessage(message: string, level?: any) {
  try {
    captureMessage(message, level)
  } catch (sentryError) {
    // Fallback to console if anything fails
    console.error('Sentry message capture failed:', sentryError)
    console.error('Original message:', message)
  }
}
